import React, { useState, useEffect, useRef, useMemo } from 'react'
import constants from '../../../../utils/constants';
import useInfiniteScroll from "../../../../components/common/useInfiniteScroll";
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom';
import { FormattedMessage, injectIntl } from 'react-intl';
import API from '../../../../utils/API';
import tableCheckbox from "../../../../components/Inputs/tableCheckbox";
import MesTable from '../../../../components/Table/MesTable';
import NotificationModal from '../../../../components/SignagePlus/NotificationModal';
import StatCard from '../../../../components/SignagePlus/StatCard';
import TotalReceiversIcon from '../../../../assets/icons/signage-plus/total-receivers-icon.svg';
import TotalUsersIcon from '../../../../assets/icons/signage-plus/total-users-icon.svg';
import TotalGroupsIcon from '../../../../assets/icons/signage-plus/total-groups-icon.svg';
import StorageUsedIcon from '../../../../assets/icons/signage-plus/storage-used-icon.svg';
import stopFilled from '../../../../assets/icons/message-manager/stopFilled.svg';
import dustbin from '../../../../assets/icons/message-manager/dustbin.svg';
import PlayPauseButton from '../../../../components/Buttons/PlayPauseButton';
import { trackEvent } from '../../../../utils/analytics';

function SignageDashboardAdmin(props) {
  const navigate = useNavigate();
  const { user } = props;
  const permissionIds = user.permissions.map((permission) => permission.id);

  if (!permissionIds.includes(constants.permissionIds.signagePlusEdit)) {
    navigate('/forbidden');
  }

  // Dashboard stats state
  const [dashboardStats, setDashboardStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);
  const hasTrackedRef = useRef(false);

  const [data, setData] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedIds, setSelectedIds] = useState({});
  const [sortByCol, setSortByCol] = useState('');
  const [sortByAsc, setSortByAsc] = useState();
  const [startIndexForTable, setStartIndexForTable] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [receiversData, setReceiversData] = useState([]);
  const [checkedKey, setCheckedKey] = useState([]);
  const [selectedKey, setSelectedKey] = useState("-1");
  const [groupsTreeData, setGroupsTreeData] = useState([]);

  const tableRef = useRef();
  const tableWrapperRef = useRef();

  const modalRef = useRef(null);
  const [notificationProps, setNotificationProps] = useState({
    message: "ASSIGN_RECEIVERS_TIPS",
    description: "ASSIGN_RECEIVERS_INFO",
    primaryButtonText: "CONTINUE",
    className: "green",
    showSecondaryButton: true,
    onSubmit: () => {},
    onClose: () => {},
  });

  // Handle play signage action
  const handlePlaySignage = async () => {
    try {
      const selectedMacs = Object.keys(selectedIds);
      if (selectedMacs.length === 0) {
        setError("Please select at least one receiver to play signage");
        return;
      }

      // Filter to only include receivers that can be played (inactive status only)
      const playableMacs = selectedMacs.filter(mac => {
        const receiver = dataMap.data[mac];
        return receiver && receiver.status === 'inactive';
      });

      if (playableMacs.length === 0) {
        setError("No selected receivers can be played (only inactive receivers can be played)");
        return;
      }

      setSuccess(""); // Clear any previous success message
      setError(""); // Clear any previous error message
      console.log("playableMacs", playableMacs);

      const response = await API.playSignagesByMacs(playableMacs);
      setSuccess(response.data.message || "Signages started successfully");
      
      // Refresh the receiver data to show updated status
      setTimeout(() => {
        loadTable(dataMap, false);
      }, 1000);
    } catch (err) {
      console.error('Error playing signages:', err);
      setError(err.response?.data?.message || "Failed to start signages");
    }
  };

  // Handle pause signage action
  const handlePauseSignage = async () => {
    try {
      const selectedMacs = Object.keys(selectedIds);
      if (selectedMacs.length === 0) {
        setError("Please select at least one receiver to pause signage");
        return;
      }

      // Filter to only include receivers that can be paused (active status only)
      const pausableMacs = selectedMacs.filter(mac => {
        const receiver = dataMap.data[mac];
        return receiver && receiver.status === 'active';
      });

      if (pausableMacs.length === 0) {
        setError("No selected receivers can be paused (only active receivers can be paused)");
        return;
      }

      setSuccess(""); // Clear any previous success message
      setError(""); // Clear any previous error message

      const response = await API.pauseSignagesByMacs(pausableMacs);
      setSuccess(response.data.message || "Signages paused successfully");
      
      // Refresh the receiver data to show updated status
      setTimeout(() => {
        loadTable(dataMap, false);
      }, 1000);
    } catch (err) {
      console.error('Error pausing signages:', err);
      setError(err.response?.data?.message || "Failed to pause signages");
    }
  };
  
  // Handle stop signage action
  const handleStopRxSignage = async (macList = null) => {
    try {
      const selectedMacs = macList || Object.keys(selectedIds);
      if (selectedMacs.length === 0) {
        setError(<FormattedMessage id="STOP_SIGNAGE_NO_RX_WARNING" defaultMessage="Please select at least one receiver to stop signage" />);
        return;
      }

      // Filter to only include receivers that can be stopped (active status only)
      const stoppableMacs = selectedMacs.filter(mac => {
        const receiver = dataMap.data[mac];
        return receiver && receiver.status === 'active';
      });

      if (stoppableMacs.length === 0) {
        setError(<FormattedMessage id="STOP_SIGNAGE_NO_ACTIVE_RX_WARNING" defaultMessage="No receivers can be stopped (only active receivers can be stopped)" />);
        return;
      }
      

      setSuccess(""); // Clear any previous success message
      setError(""); // Clear any previous error message

      const response = await API.stopSignagesByMacs(stoppableMacs);
      setSuccess(response.data.message || <FormattedMessage id="SIGNAGE_STOPPED_SUCCESSFULLY" defaultMessage="Signages stopped successfully" />);
      
      // Refresh the receiver data to show updated status
      setTimeout(() => {
        loadTable(dataMap, false);
      }, 1000);
    } catch (err) {
      console.error('Error pausing signages:', err);
      setError(err.response?.data?.message || <FormattedMessage id="SIGNAGE_STOPPED_FAILED" defaultMessage="Failed to stop signages" />);
    }
  };

  // Fetch dashboard stats
  const fetchDashboardStats = async () => {
    try {
      setStatsLoading(true);
      setStatsError(null);
      const response = await API.getSignageDashboardSummary();
      console.log(response.data);
      setDashboardStats(response.data);

      // Track dashboard view once on initial load
      if (!hasTrackedRef.current && response.data) {
        hasTrackedRef.current = true;
        const stats = response.data;
        trackEvent('signage.dashboard_admin', {
          total_receivers: (stats.totalActive || 0) + (stats.totalIdel || 0) + (stats.totalOffline || 0),
          active_receivers: stats.totalActive || 0,
          idle_receivers: stats.totalIdel || 0,
          offline_receivers: stats.totalOffline || 0
        }, user);
      }
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setStatsError(err.message || 'Failed to load dashboard statistics');
    } finally {
      setStatsLoading(false);
    }
  };

  // Effect for fetching dashboard stats
  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const loadTable = async (map, resetSelection = true) => {
    map.data = {};
    map.ids = [];
    map.sortedIds = [];
    map.filteredIds = [];
    if (resetSelection) {
      map.selectedIds = {};
    }
    try {
      const res = await API.getDashboardReceivers();
      console.log(res.data);
      res.data.forEach((receivers) => {
        // Calculate status based on sbds_last_seen and in-process status
        const lastSeen = new Date(receivers.sbdsLastSeen || 0);
        const currentTime = new Date();
        const fiveMinutesAgo = new Date(currentTime - 5 * 60 * 1000); // 5 minutes ago

        map.data[receivers.mac] = {
          mac: receivers.mac,
          name: receivers.name,
          groupsName: receivers.groups?.map((group) => group.name).join(", "),
          signagesName: receivers.signages?.map((signage) => signage.name).join(", "),
          status: receivers.status === 'idel' ? 'idle' : receivers.status,
          checked: false,
        };
      });
      map.filteredIds = Object.keys(map.data);
      map.ids = Object.keys(map.data);
      setReceiversData(res.data);
      setDataCount(map.ids.length);
      setSelectedIds(map.selectedIds);
      setTotalDataCount(map.ids.length);
      setDataLoaded(true);
    } catch (err) {
      console.log(err);
      setDataLoaded(true);
      if (err.response?.data?.error) {
        return;
      }
      setError(err.message);
    }
  };

  const closeModal = (modalId) => {
    const modal = document.getElementById(modalId);
    if (modal) {
      const closeButton = modal.querySelector(
        '.btn-close, [data-bs-dismiss="modal"]'
      );
      if (closeButton) {
        closeButton.click();
      }
    }
    //fetchGroupsTreeData();
  };

  // Helper function to check if play action is available for selected receivers
  const canPlaySelectedReceivers = () => {
    const selectedMacs = Object.keys(selectedIds);
    return selectedMacs.some(mac => {
      const receiver = dataMap.data[mac];
      return receiver && receiver.status === 'inactive';
    });
  };

  // Helper function to check if pause action is available for selected receivers
  const canPauseSelectedReceivers = () => {
    const selectedMacs = Object.keys(selectedIds);
    return selectedMacs.some(mac => {
      const receiver = dataMap.data[mac];
      return receiver && receiver.status === 'active';
    });
  };

  // Handle play confirmation modal
  const handlePlayConfirmation = () => {
    const selectedMacs = Object.keys(selectedIds);
    if (selectedMacs.length === 0) {
      setError("Please select at least one receiver to play signage");
      return;
    }

    if (!canPlaySelectedReceivers()) {
      setError("Selected receivers cannot be played (only inactive receivers can be played)");
      return;
    }

    setNotificationProps({
      message: "CONFIRM_PLAY_SIGNAGE",
      description: "CONFIRM_PLAY_SIGNAGE_DESC", 
      primaryButtonText: "PLAY",
      secondaryButtonText: "CANCEL",
      className: "green",
      showSecondaryButton: true,
      onSubmit: () => {
        closeModal('notificationModal');
        handlePlaySignage();
      },
      onClose: () => closeModal('notificationModal'),
    });
  };

  // Handle pause confirmation modal
  const handlePauseConfirmation = () => {
    const selectedMacs = Object.keys(selectedIds);
    if (selectedMacs.length === 0) {
      setError("Please select at least one receiver to pause signage");
      return;
    }

    if (!canPauseSelectedReceivers()) {
      setError("Selected receivers cannot be paused (only active receivers can be paused)");
      return;
    }

    setNotificationProps({
      message: "CONFIRM_PAUSE_SIGNAGE",
      description: "CONFIRM_PAUSE_SIGNAGE_DESC",
      primaryButtonText: "PAUSE",
      secondaryButtonText: "CANCEL", 
      className: "red",
      showSecondaryButton: true,
      onSubmit: () => {
        closeModal('notificationModal');
        handlePauseSignage();
      },
      onClose: () => closeModal('notificationModal'),
    });
  };
  // Handle stop confirmation modal
  const handleStopConfirmation = (macList = null) => {
    if(!macList){
      const selectedMacs = Object.keys(selectedIds);
      if (selectedMacs.length === 0) {
        setError(<FormattedMessage id="STOP_SIGNAGE_NO_RX_WARNING" defaultMessage="Please select at least one receiver to stop signage" />);
        return;
      }

      if (!canPauseSelectedReceivers()) {
        setError(<FormattedMessage id="STOP_SIGNAGE_NO_ACTIVE_RX_WARNING" defaultMessage="No receivers can be stopped (only active receivers can be stopped)" />);
         return;
      }
    }
    setNotificationProps({
      icon: dustbin,
      message: "STOP_SIGNAGE",
      description: "STOP_SIGNAGE_DESCRIPTION", 
      primaryButtonText: "STOP",
      secondaryButtonText: "CANCEL",
      className: "green",
      showSecondaryButton: true,
      onSubmit: (e) => {
        e.preventDefault();
        closeModal('notificationModal');
        handleStopRxSignage(macList);
      },
      onClose: () => closeModal('notificationModal'),
    });
  };
  const dataMap = useMemo(() => {
    const map = {
      data: {},
      ids: [],
      selectedIds: {},
      filteredIds: [],
      sortedIds: [],
    };
    loadTable(map);
    return map;
  }, []);

  const parseGroupsData = (groupsData) => {
    return groupsData.map((node) => ({
      title: node.name,
      key: node.id,
    }));
  };

  const fetchGroupsTreeData = async () => {
    const groups = await API.getGroups();
    const parsedData = parseGroupsData(groups.data);
    setGroupsTreeData(parsedData);
  };

  // Effect for selectedKey changes
  useEffect(() => {
    console.log("selectedKey changed", selectedKey);
    loadTable(dataMap);
  }, [selectedKey]);
  
  // Effect for interval refresh (runs once when component mounts)
  useEffect(() => {
    console.log("Setting up interval refresh");
    
    const intervalId = setInterval(() => {
      console.log("Refreshing receiver status data on interval");     
      loadTable(dataMap, false); // Don't reset selection on refresh
      fetchDashboardStats(); // Refresh dashboard stats
    }, 30000); // 30 seconds
    
    return () => {
      console.log("Clearing interval refresh");
      clearInterval(intervalId);
    };
  }, []);

  // Hook for infinite scrolling
  const { generateDataSlice, sortMap, setSortBy } = useInfiniteScroll({
    dataMap,
    setData,
    setStartIndexForTable,
    startIndexForTable,
    pageSize: 100,
    sortByCol,
    setSortByCol,
    sortByAsc,
    setSortByAsc,
    tableWrapperRef,
    searchValue,
    tableRef,
    rowHeight: 48,
  });

  useEffect(() => {
    if (dataLoaded) {
      if (searchValue !== "") {
        tableRef.current.search(dataMap);
      } else {
        sortMap(dataMap);
        setData(generateDataSlice(dataMap));
      }
    }
  }, [sortByCol, sortByAsc, dataLoaded, dataMap, receiversData]);

  // Helper function to get status-based styling
  const getStatusStyle = (status) => {
    if (status === 'active') {
      return { color: '#36B199' };
    } else if (status === 'idle') {
      return { color: '#2196F3' };
    }
    return {};
  };

  const tableColumnArray = [
    {
      Header: tableCheckbox,
      id: "checkbox",
      accessor: "checked",
      Cell: tableCheckbox,
      isVisible: true,
      width: 63,
      maxWidth: 63,
    },
    {
      Header: "RECEIVER_NAME",
      id: "name",
      accessor: "name",
      Cell: ({ row }) => (
        <span style={getStatusStyle(row.original.status)}>
          {row.original.name}
        </span>
      ),
      isVisible: true,
    },
    {
      Header: "ASSIGNED_GROUP",
      id: "assignedGroup",
      accessor: "groupsName",
      Cell: ({ row }) => (
        <span style={getStatusStyle(row.original.status)}>
          {row.original.groupsName}
        </span>
      ),
      isVisible: true,
    },
    {
      Header: "CURRENT_STATUS",
      id: "status",
      accessor: "status",
      Cell: ({ row }) => {
        const status = row.original.status;
        const statusMessages = {
          'active': 'ACTIVE',
          'inactive': 'INACTIVE',
          'idle': 'IDLE',
          'offline': 'OFFLINE',
          'unknown': 'UNKNOWN'
        };
        
        return (
          <span className={`receiver-${status}`} style={getStatusStyle(status)}>
            <FormattedMessage id={statusMessages[status] || status.toUpperCase()} />
          </span>
        );
      },
      isVisible: true,
    },
    {
      Header: "CURRENT_SIGNAGE_PLAYING",
      id: "currentSignagePlaying",
      accessor: "signagesName",
      Cell: ({ row }) => (
        <span style={getStatusStyle(row.original.status)}>
          {row.original.signagesName}
        </span>
      ),
      isVisible: true,
    },
    {
      Header: '',
      id: "actions",
      Cell: ({ row }) => {
        // Determine if button should be disabled
        const isDisabled = !(row.original.status === 'active');
        return ( !isDisabled &&
          <div className="actions d-flex align-items-center">
            <button
              className="btn"
              id="stopMessage"
              data-bs-toggle="modal"
              data-bs-target="#notificationModal"
              onClick={(e) => {
                e.stopPropagation(); // Prevent click from propagating to the parent cell 
                handleStopConfirmation([row.original.mac]);             
              }}
            >
              <img 
                src={stopFilled} 
                className="deleteAction" 
                alt={<FormattedMessage id="STOP" />}
              />
           </button>
          </div>     
        );
      },
      /*  {
        const status = row.original.status;
        const mac = row.original.mac;
        
        // Handle individual receiver play/stop actions
        const handleIndividualPlay = async () => {
          if (status !== 'inactive') {
            setError("This receiver cannot be played (only inactive receivers can be played)");
            return;
          }
          
          try {
            setSuccess("");
            setError("");
            const response = await API.playSignagesByMacs([mac]);
            setSuccess(response.data.message || "Signage started successfully");
            
            setTimeout(() => loadTable(dataMap, false), 1000);
          } catch (err) {
            console.error('Error playing signage:', err);
            setError(err.response?.data?.message || "Failed to start signage");
          }
        };

        const handleIndividualPause = async () => {
          if (status !== 'active') {
            setError("This receiver cannot be paused (only active receivers can be paused)");
            return;
          }
          
          try {
            setSuccess("");
            setError("");
            const response = await API.pauseSignagesByMacs([mac]);
            setSuccess(response.data.message || "Signage paused successfully");
            
            setTimeout(() => loadTable(dataMap, false), 1000);
          } catch (err) {
            console.error('Error pausing signage:', err);
            setError(err.response?.data?.message || "Failed to pause signage");
          }
        };



        return (
          <PlayPauseButton 
            status={status} 
            onPlay={handleIndividualPlay}
            onPause={handleIndividualPause}
          />
        );
      },*/
      isVisible: true,
      mWidth: "mWidth-60",
    }
  ];

  const buttons = {
    footerButtons: (
      <>
        <button
          id="playDigitalSignageBtn"
          type="button"
          className="btn"
          data-bs-toggle="modal"
          data-bs-target="#notificationModal"
          disabled={Object.keys(selectedIds).length === 0 || !canPlaySelectedReceivers()}
          onClick={handlePlayConfirmation}
          style={{display: 'none'}}
        >
          <span>
            <FormattedMessage id="PLAY_DIGITAL_SIGNAGE" />
          </span>
        </button>

        <button
          id="pauseDigitalSignageBtn"
          type="button"
          className="btn"
          data-bs-toggle="modal"
          data-bs-target="#notificationModal"
          disabled={Object.keys(selectedIds).length === 0 || !canPauseSelectedReceivers()}
          onClick={handlePauseConfirmation}
          style={{display: 'none'}}
        >
          <span>
            <FormattedMessage id="PAUSE_DIGITAL_SIGNAGE" />
          </span>
        </button>
        <button
          id="stopDigitalSignageBtn"
          type="button"
          className="btn"
          data-bs-toggle="modal"
          data-bs-target="#notificationModal"
          disabled={Object.keys(selectedIds).length === 0 || !canPauseSelectedReceivers()}
          onClick={() => handleStopConfirmation()}
        >
          <span>
            <FormattedMessage id="STOP_DIGITAL_SIGNAGE" />
          </span>
        </button>
      </>
    ),
  };

  const tableProps = {
    // The tableKey is for task#20542.
    // Each table requires a unique key to prevent preferences from being overwritten.
    tableKey: "sbds_receivers_status_table",
    pageType: constants.pageType.ReceiversAndSignageControl,
    modalId: null,
    columns: tableColumnArray,
    data,
    setData,
    dataMap,
    generateDataSlice,
    sortMap,
    dataCount,
    setDataCount,
    totalDataCount,
    setSortBy,
    sortByCol,
    sortByAsc,
    success,
    setSuccess,
    error,
    setError,
    startIndex: startIndexForTable,
    selectedIds,
    setSelectedIds,
    searchValue,
    setSearchValue,
    tableRef,
    tableWrapperRef,
    dataLoaded,
    buttons,
  };

  const totalReceiversStatCardProps = {
    icon: TotalReceiversIcon,
    title: "TOTAL_RECEIVERS",
    number: statsLoading 
      ? [0, 0, 0] 
      : [
          dashboardStats?.totalActive || 0,
          dashboardStats?.totalIdel || 0,
          //dashboardStats?.totalInactive || 0, 
          dashboardStats?.totalOffline || 0
        ],
    unit: ["ACTIVE", "IDLE", /*"INACTIVE",*/ "OFFLINE"],
    colors: ["#36B199", "#2196F3", /*"#000000",*/ "#EC1C24"],
    width: "35%",
    loading: statsLoading,
    error: statsError,
  };

  const totalUsersStatCardProps = {
    icon: TotalUsersIcon,
    title: "TOTAL_USERS",
    number: statsLoading ? [0] : [dashboardStats?.totalUsers || 0],
    colors: ["#005083"],
    width: "18%",
    loading: statsLoading,
    error: statsError,
  };
  
  const totalGroupsStatCardProps = {
    icon: TotalGroupsIcon,
    title: "TOTAL_GROUPS",
    number: statsLoading ? [0] : [dashboardStats?.totalGroups || 0],
    colors: ["#005083"],
    width: "18%",
    loading: statsLoading,
    error: statsError,
  };

  const storageUsedStatCardProps = {
    icon: StorageUsedIcon,
    title: "STORAGE_USED",
    number: statsLoading 
      ? [0.00, 0] 
      : [
          dashboardStats?.totalConsumption?.toFixed(2) || 0.00,
          dashboardStats?.totalCapacity || 0
        ],
    unit: ["GB", "GB"],
    colors: ["#005083", "#005083"],
    width: "27%",
    loading: statsLoading,
    error: statsError,
  };

  return (
    <>
      <div className="d-flex flex-row justify-content-between pb-4">
        <StatCard {...totalReceiversStatCardProps}/>
        <StatCard {...totalUsersStatCardProps}/>
        <StatCard {...totalGroupsStatCardProps}/>
        <StatCard {...storageUsedStatCardProps}/>
      </div>
      <NotificationModal {...notificationProps} />
      <MesTable {...tableProps} />
    </>
  )
}

const mapStateToProps = (state) => ({
  user: state.user
})

export default connect(mapStateToProps)(injectIntl(SignageDashboardAdmin))
